'use client'

import { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@/lib/zod-resolver'
import { z } from 'zod/v4'
import { Shield, Eye, EyeOff, Loader2, Mail, KeyRound, Fingerprint, Users, ArrowRight } from 'lucide-react'
import { startAuthentication } from '@simplewebauthn/browser'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { authApi, type LoginResponse } from '@/lib/api/auth'
import { useAuthStore } from '@/stores/auth-store'
import { useTranslation } from '@/lib/i18n'
import { LanguageSwitcher } from '@/components/language-switcher'
import { BrandedLoader } from '@/components/branded-loader'

// ── Validation Schemas ──────────────────────────────────────

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

const otpEmailSchema = z.object({
  email: z.email('Please enter a valid email address'),
})

const otpVerifySchema = z.object({
  email: z.email('Please enter a valid email address'),
  otp: z.string().length(6, 'OTP must be 6 digits'),
})

type LoginFormData = z.infer<typeof loginSchema>
type OtpEmailData = z.infer<typeof otpEmailSchema>
type OtpVerifyData = z.infer<typeof otpVerifySchema>

// ── Safe redirect helper ────────────────────────────────────

/**
 * Resolve the post-login redirect target from the `next` query param, with
 * open-redirect protection. The producer is the accept-invite page, which
 * sends `?next=<encoded internal path>` so the user lands back on
 * `/accept-invite?token=…` after authenticating (where its sessionStorage
 * pending-token logic finishes the accept).
 *
 * Only SAFE INTERNAL paths are honored: the value must start with a single
 * `/` and must NOT begin with `//` or `/\` (protocol-relative / backslash
 * tricks that browsers treat as absolute external URLs — the classic
 * open-redirect vector). Anything else (absolute `http(s)://`, scheme-relative,
 * empty, or malformed) falls back to the default `/recipients`.
 */
function resolveSafeNext(raw: string | null): string {
  const FALLBACK = '/recipients'
  if (!raw) return FALLBACK
  // `next` may arrive percent-encoded (the producer encodes the whole value).
  let next = raw
  try {
    next = decodeURIComponent(raw)
  } catch {
    // Malformed percent-encoding → reject.
    return FALLBACK
  }
  // Must be a same-origin absolute path: starts with "/", but not "//" or "/\"
  // (both of which browsers resolve to a different origin → open redirect).
  if (!next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) {
    return FALLBACK
  }
  return next
}

/**
 * Build the OAuth start URL, telling the backend which host to send the browser BACK to.
 *
 * This one app is served on several product hosts (dash / play / portal / educate). Without
 * `redirectTo`, the callback always bounced to APP_BASE_URL — so an employee signing in on the
 * play/portal host was dumped on the ADMIN console. We pass the origin we actually started on;
 * the backend allowlists it (`AUTH_ALLOWED_REDIRECT_ORIGINS`) and falls back to APP_BASE_URL if
 * it is not trusted, so this can never become an open redirect.
 */
function ssoStartUrl(provider: 'microsoft' | 'google'): string {
  const api = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001'
  const base = `${api}/v1/auth/${provider}`
  if (typeof window === 'undefined') return base
  return `${base}?redirectTo=${encodeURIComponent(window.location.origin)}`
}

// ── Component ───────────────────────────────────────────────

function LoginPageInner() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpEmail, setOtpEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Auth-redirect gate. While true, render <BrandedLoader/> instead of the
  // login form so authed users never flash the unauthed UI on direct hits
  // to /login. Closes the "login page shows even when logged in" tester
  // report (2026-05-08). The companion SSR redirect at app/page.tsx covers
  // the / → /login bounce; this gate covers direct /login navigations.
  const [authGateChecking, setAuthGateChecking] = useState(true)

  // Email + Password form
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  // OTP email form
  const otpEmailForm = useForm<OtpEmailData>({
    resolver: zodResolver(otpEmailSchema),
    defaultValues: { email: '' },
  })

  // OTP verify form
  const otpVerifyForm = useForm<OtpVerifyData>({
    resolver: zodResolver(otpVerifySchema),
    defaultValues: { email: '', otp: '' },
  })

  // Redirect if already authenticated. While the cookie check runs (and
  // while a positive-match redirect is in flight) we render <BrandedLoader/>
  // below instead of the login form so the unauthed UI never flashes for
  // logged-in users.
  useEffect(() => {
    const token = document.cookie.includes('hf-app:access-token')
    if (token) {
      // Already authenticated → honor a safe `next` (e.g. the accept-invite
      // round-trip) instead of always bouncing to /recipients.
      router.push(resolveSafeNext(searchParams.get('next')))
      // Keep the gate `true` so the loader stays on screen until the
      // navigation completes (replacing the page entirely).
      return
    }
    setAuthGateChecking(false)
  }, [router, searchParams])

  // ── Handlers ────────────────────────────────────────────

  const handleLoginSuccess = async (response: LoginResponse) => {
    // Backend returns { user, tokens: { accessToken, refreshToken } }
    // OTP flow returns { accessToken, refreshToken } at top level (no user/tokens wrapper)
    // Password flow returns { user, tokens: { accessToken, refreshToken } }
    const user = (response.user ?? {}) as unknown as Record<string, string>;
    const accessToken = response.tokens?.accessToken ?? response.accessToken ?? '';
    const refreshToken = response.tokens?.refreshToken ?? response.refreshToken ?? '';

    // Store tokens immediately so we can make authenticated requests
    if (typeof window !== 'undefined') {
      localStorage.setItem('hf-app:access-token', accessToken);
      localStorage.setItem('hf-app:refresh-token', refreshToken);
    }

    // Fetch workspaces to get the workspace ID (not returned in login)
    let workspaceId: string | undefined;
    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001';
      const wsResponse = await fetch(`${API_URL}/v1/workspaces/my`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      if (wsResponse.ok) {
        const workspaces = await wsResponse.json();
        const wsList = Array.isArray(workspaces) ? workspaces : workspaces.data || [];
        if (wsList.length > 0) {
          workspaceId = wsList[0].id;
          localStorage.setItem('hf-app:workspace-id', workspaceId!);
        }
      }
    } catch {
      // Workspace fetch failed — proceed without it
    }

    const loginData = {
      user: user as unknown as Parameters<ReturnType<typeof useAuthStore.getState>['login']>[0]['user'],
      accessToken,
      refreshToken,
      workspaceId,
    };
    useAuthStore.getState().login(loginData);
    toast.success(t('auth.loginSuccess'), {
      description: t('auth.welcomeBackName', { name: user?.firstName || user?.email || 'there' }),
    });
    // Accounts provisioned with a one-time temp password must set a real
    // password before doing anything else (takes precedence over `next`).
    if (user?.mustChangePassword) {
      router.push('/auth/set-password');
      return;
    }
    // Honor a safe `next` (e.g. accept-invite → login → return-and-accept)
    // instead of always landing on /recipients. Open-redirect-guarded.
    router.push(resolveSafeNext(searchParams.get('next')));
  }

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true)
    try {
      const response = await authApi.login(data.email, data.password)
      handleLoginSuccess(response)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed. Please check your credentials.'
      toast.error(t('auth.loginFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSendOtp = async (data: OtpEmailData) => {
    setIsSubmitting(true)
    try {
      await authApi.sendOtp(data.email)
      setOtpEmail(data.email)
      setOtpSent(true)
      otpVerifyForm.setValue('email', data.email)
      toast.success(t('auth.otpSent'), {
        description: t('auth.otpSentDesc', { email: data.email }),
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : t('auth.failedSendOtp')
      toast.error(t('auth.failedSendOtp'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onVerifyOtp = async (data: OtpVerifyData) => {
    setIsSubmitting(true)
    try {
      const response = await authApi.verifyOtp(data.email, data.otp)
      handleLoginSuccess(response)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid or expired OTP'
      toast.error(t('auth.verificationFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onPasskeyLogin = async () => {
    setIsSubmitting(true)
    try {
      // 1. Get authentication options from backend
      const { options } = await authApi.passkeyLoginOptions()
      // 2. Trigger browser biometric prompt
      const authResponse = await startAuthentication({ optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'] })
      // 3. Send response to backend for verification + JWT
      const response = await authApi.passkeyLoginVerify(authResponse as unknown as Parameters<typeof authApi.passkeyLoginVerify>[0])
      handleLoginSuccess(response)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('auth.passkeyFailed')
      toast.error(t('auth.passkeyFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Render ──────────────────────────────────────────────

  // Auth gate: while we're checking the cookie (and during a logged-in
  // redirect) show the branded loader so the form never flashes for
  // already-authenticated users. See the useEffect above.
  if (authGateChecking) {
    return <BrandedLoader />
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
      {/* Top header cluster — Employee Portal entry + Language Switcher.
          Why this lives here: the admin login is the wrong door for an
          employee; without a visible "go to employee portal" affordance
          they bounce off and call IT. Theme tokens (bg-card, primary,
          muted-foreground) come from the same shadcn token set the portal
          login uses — they render identically in both light + dark mode.
          Why /portal/login: the portal login page handles its own
          ?ws=<workspaceId> query-param + sessionStorage workspace
          resolution chain (see app/(portal)/layout.tsx), so the admin
          login doesn't need to pre-resolve a workspace. */}
      <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2">
        <Link
          href="/portal/login"
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card hover:bg-muted text-sm font-medium px-3 py-1.5 text-foreground transition-colors"
          aria-label="Go to Employee Portal sign-in"
          data-testid="admin-login-go-to-portal"
        >
          <Users className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="hidden sm:inline">Employee Portal</span>
          <ArrowRight className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
        </Link>
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="flex flex-col items-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-primary/10 text-primary mb-3 sm:mb-4">
            <Shield className="h-7 w-7 sm:h-8 sm:w-8" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{t('auth.brand')}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 text-center px-2">
            {t('auth.tagline')}
          </p>
        </div>

        {/* Login Card */}
        <Card>
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl">{t('auth.welcomeBack')}</CardTitle>
            <CardDescription>
              {t('auth.signInDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="password" className="w-full">
              <TabsList className="w-full mb-6 h-auto p-1">
                <TabsTrigger
                  value="password"
                  className="flex-1 gap-1 sm:gap-1.5 px-1 sm:px-3 py-1.5 text-xs sm:text-sm min-w-0"
                  aria-label={t('auth.emailPassword')}
                >
                  <KeyRound className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline truncate">{t('auth.emailPassword')}</span>
                  <span className="sm:hidden">Password</span>
                </TabsTrigger>
                <TabsTrigger
                  value="otp"
                  className="flex-1 gap-1 sm:gap-1.5 px-1 sm:px-3 py-1.5 text-xs sm:text-sm min-w-0"
                  aria-label={t('auth.otpPasswordless')}
                >
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline truncate">{t('auth.otpPasswordless')}</span>
                  <span className="sm:hidden">OTP</span>
                </TabsTrigger>
                <TabsTrigger
                  value="passkey"
                  className="flex-1 gap-1 sm:gap-1.5 px-1 sm:px-3 py-1.5 text-xs sm:text-sm min-w-0"
                  aria-label={t('auth.passkey')}
                >
                  <Fingerprint className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{t('auth.passkey')}</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Email + Password */}
              <TabsContent value="password">
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      autoComplete="email"
                      {...loginForm.register('email')}
                      aria-invalid={!!loginForm.formState.errors.email}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">{t('auth.password')}</Label>
                      <Link
                        href="/auth/forgot-password"
                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                      >
                        {t('auth.forgotPassword')}
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('auth.passwordPlaceholder')}
                        autoComplete="current-password"
                        className="pr-10"
                        {...loginForm.register('password')}
                        aria-invalid={!!loginForm.formState.errors.password}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('auth.signingIn')}
                      </>
                    ) : (
                      t('auth.signIn')
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Tab 2: OTP (Passwordless) */}
              <TabsContent value="otp">
                {!otpSent ? (
                  <form onSubmit={otpEmailForm.handleSubmit(onSendOtp)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp-email">{t('auth.email')}</Label>
                      <Input
                        id="otp-email"
                        type="email"
                        placeholder={t('auth.emailPlaceholder')}
                        autoComplete="email"
                        {...otpEmailForm.register('email')}
                        aria-invalid={!!otpEmailForm.formState.errors.email}
                      />
                      {otpEmailForm.formState.errors.email && (
                        <p className="text-sm text-destructive">
                          {otpEmailForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground">
                      {t('auth.otpDescription')}
                    </p>

                    <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t('auth.sendingOtp')}
                        </>
                      ) : (
                        t('auth.sendOtp')
                      )}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={otpVerifyForm.handleSubmit(onVerifyOtp)} className="space-y-4">
                    <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                      <p className="text-muted-foreground">
                        {t('auth.otpSentDesc', { email: otpEmail })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="otp-code">{t('auth.verificationCode')}</Label>
                      <Input
                        id="otp-code"
                        type="text"
                        inputMode="numeric"
                        placeholder="000000"
                        maxLength={6}
                        className="text-center text-lg tracking-[0.5em] font-mono"
                        {...otpVerifyForm.register('otp')}
                        aria-invalid={!!otpVerifyForm.formState.errors.otp}
                      />
                      {otpVerifyForm.formState.errors.otp && (
                        <p className="text-sm text-destructive">
                          {otpVerifyForm.formState.errors.otp.message}
                        </p>
                      )}
                    </div>

                    <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t('auth.verifying')}
                        </>
                      ) : (
                        t('auth.verifyOtp')
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-1">
                      <span className="text-xs text-muted-foreground">{t('auth.didntReceiveCode')}</span>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => {
                          setOtpSent(false)
                          otpVerifyForm.reset()
                        }}
                      >
                        {t('auth.sendAgain')}
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>

              {/* Tab 3: Passkey (Biometric) */}
              <TabsContent value="passkey">
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground">
                    {t('auth.passkeyDescription')}
                  </p>

                  <Button
                    type="button"
                    className="w-full h-11 sm:h-10"
                    disabled={isSubmitting}
                    onClick={onPasskeyLogin}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('auth.verifying')}
                      </>
                    ) : (
                      <>
                        <Fingerprint className="h-4 w-4 mr-2" />
                        {t('auth.signInWithPasskey')}
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* OAuth Social Login */}
        <div className="mt-6 space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              type="button"
              className="h-11 sm:h-10"
              onClick={() => {
                window.location.href = ssoStartUrl('microsoft')
              }}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21" fill="none">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              {t('auth.microsoft')}
            </Button>
            <Button
              variant="outline"
              type="button"
              className="h-11 sm:h-10"
              onClick={() => {
                window.location.href = ssoStartUrl('google')
              }}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t('auth.google')}
            </Button>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-6 space-y-3 text-center">
          <p className="text-sm text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link
              href="/auth/register"
              className="text-primary font-medium hover:underline"
            >
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

// Wrap in Suspense: LoginPageInner calls useSearchParams (to read `next`),
// which Next 16 requires to be inside a Suspense boundary. The fallback
// mirrors the auth-gate loader so there's no UI flash on the boundary.
export default function LoginPage() {
  return (
    <Suspense fallback={<BrandedLoader />}>
      <LoginPageInner />
    </Suspense>
  )
}
